import { Fragment } from "react";
import { MarkerIcon } from "../shared/markers";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import type { PartnerMapAction } from "./track";
import type { PartnerInfo, PartnerMapContent, WeekEvent } from "./types";
import styles from "./HostelPanel.module.scss";

interface HostelPanelProps {
	content: PartnerMapContent;
	info: PartnerInfo;
	/** what is on at the hostel today: the events the Highlights tab's "Today at the hostel" row is made from */
	atHostel: WeekEvent[];
	onTrack: (action: PartnerMapAction, item?: string) => void;
}

/**
 * The body of the hostel's panel in the lit look, under its address and name (`Sheet`): what is on tonight, one big button to
 * book, three small links (a missing one is left out) and the next stops. No socials and no "Start planning": the socials end
 * the Highlights tab and "Save this map" is at the foot of the page.
 */
const HostelPanel = ({ content, info, atHostel, onTrack }: HostelPanelProps) => {
	const { partner, nextStops } = content;
	const t = strings(content.lang);
	const links = [
		info.events && { key: "events", href: info.events, label: t.events, icon: "tabWeek" },
		info.website && { key: "website", href: info.website, label: t.hostelPage, icon: "globe" },
		info.directions && { key: "directions", href: info.directions, label: t.directions, icon: "directions" },
	].filter((link): link is NonNullable<typeof link> => !!link);
	const stops = nextStops?.stops ?? [];
	return (
		<>
			{atHostel.length > 0 && (
				<div className={styles.tonightBlock}>
					<h3>{t.tonightAtHostel}</h3>
					<ul>
						{atHostel.map((e) => (
							<li key={e.id}>
								<b>{e.title}</b>
								<span>{e.time}</span>
							</li>
						))}
					</ul>
				</div>
			)}
			{info.book && (
				<a
					id="partnermap-sheet-info-book"
					className={styles.bookBtn}
					href={info.book}
					target="_blank"
					rel="noopener"
					onClick={() => onTrack("contact", "book")}
				>
					<MarkerIcon name="bed" size={18} />
					{t.bookBed}
				</a>
			)}
			{links.length > 0 && (
				<div className={styles.pills}>
					{links.map((link) => (
						<a
							key={link.key}
							id={`partnermap-sheet-info-${link.key}`}
							href={link.href}
							target="_blank"
							rel="noopener"
							onClick={() => onTrack("contact", link.key)}
						>
							<UiIcon name={link.icon} />
							{link.label}
						</a>
					))}
				</div>
			)}
			{stops.length > 0 && (
				<div className={styles.next}>
					{t.nextStops}{" "}
					{stops.map((stop, i) => (
						<Fragment key={stop.name}>
							{i > 0 && " · "}
							{/* the card's own link, under the card's own name without the partner's */}
							<a href={stop.url} target="_blank" rel="noopener" onClick={() => onTrack("contact", "next_stop")}>
								{stop.name.replace(`${partner.shortName} `, "")}
							</a>
						</Fragment>
					))}
				</div>
			)}
		</>
	);
};

export default HostelPanel;
